/* eslint-disable react/jsx-props-no-spreading */
import { PhoneNumber } from 'libphonenumber-js'
import { useCallback, memo } from 'react'
import { usePatchedField } from 'stores/utils/hooks/usePatchedField'
import { useStores } from 'stores/utils/hooks/useStores'
import { observer } from 'mobx-react'
import { isMobileNumberValidOnGreen } from 'utils/number'
import { Debtor } from 'containers/PersonalForm/PersonalForm.interface'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { progressiveDisclosureValidator, VALIDATOR_PHONE } from 'validation'
import FieldDataCollector from '../FieldDataCollector'
import { PathFormFieldProps } from '../../interfaces/PathFormComponents.interface'
import WithFieldInteraction from '../../utils/tracking/amplitude/WithFieldInteraction'
import { useMeta } from '../../stores/utils/hooks/useMeta'
import Phone from '../Input/Phone'

type Props = PathFormFieldProps<PhoneNumber | null> & {
  debtor?: Debtor
  isSmava?: boolean
  trackOnChange?: boolean
}

const PhoneInputClass = observer(
  ({
    path,
    trackOnChange,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    labelPath,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    defaultValue,
    onChange,
    isSmava,
    ...props
  }: Props) => {
    const { input, error } = usePatchedField(
      path,
      {},
      true,
      progressiveDisclosureValidator(VALIDATOR_PHONE)
    )
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { errorMap, ...restMeta } = useMeta(path)

    const {
      traversal: { removeErrorsForPath }
    } = useStores()

    const handleChange = useCallback(
      (_: PhoneNumber | null, raw: string | undefined) => {
        input.onChange(raw)
        if (onChange && raw) {
          onChange(raw as unknown as PhoneNumber)
        }
        removeErrorsForPath(path)
      },
      [input, removeErrorsForPath, path, onChange]
    )

    const handleBlur = useCallback(
      (phone: PhoneNumber | null, raw: string | undefined) => {
        input.onChange(phone?.number || raw)
        input.onBlur()
      },
      [input]
    )
    const handleFocus = useCallback(() => {
      input.onFocus()
    }, [input])

    if (error && isMobileNumberValidOnGreen(input.value) && isSmava) {
      trackInAmplitude(AmplitudeEvent.OnPhoneValidForGreen)
    }

    return (
      <WithFieldInteraction path={path} trackOnChange={trackOnChange}>
        <Phone
          {...input}
          {...restMeta}
          {...props}
          id={path}
          error={error}
          value={input.value}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
        />
      </WithFieldInteraction>
    )
  }
)

const PathPhoneInputClass = (props: Props) => {
  const { path, labelPath } = props

  return (
    <FieldDataCollector path={path} labelPath={labelPath}>
      <PhoneInputClass {...props} />
    </FieldDataCollector>
  )
}

export default memo(PathPhoneInputClass)
